import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import LoadingState from '../components/LoadingState';
import ErrorState from '../components/ErrorState';
import { couriers, deliveryFees, deliveryServices, fees, stores } from '../../services/apiClient';

function normalizeKey(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();
}

const UF_CODES = new Set([
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
  'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
  'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
]);

function parseCityRegion(address) {
  const raw = String(address || '').trim();
  if (!raw) return { city: '', region: '' };

  const parts = raw.split(/[,\|]/).map((part) => part.trim()).filter(Boolean);
  if (!parts.length) return { city: '', region: '' };

  let city = '';
  let region = '';
  let regionIndex = -1;

  for (let i = parts.length - 1; i >= 0; i -= 1) {
    const match = parts[i].match(/(.+?)\s*[-/]\s*([A-Za-z]{2})(?:\b|[^A-Za-z].*)$/);
    if (match) {
      const uf = String(match[2] || '').toUpperCase();
      if (UF_CODES.has(uf)) {
        city = match[1].trim();
        region = uf;
        regionIndex = i;
        break;
      }
    }
  }

  if (!region) {
    for (let i = parts.length - 1; i >= 0; i -= 1) {
      const match = parts[i].match(/\b([A-Za-z]{2})\b/);
      if (!match) continue;
      const uf = String(match[1] || '').toUpperCase();
      if (UF_CODES.has(uf)) {
        region = uf;
        regionIndex = i;
        break;
      }
    }
  }

  if (!city) {
    const cepIndex = parts.findIndex((part) => /\b\d{5}-?\d{3}\b/.test(part));
    if (cepIndex >= 0 && cepIndex + 1 < parts.length) {
      city = parts[cepIndex + 1];
    } else if (regionIndex > 0) {
      city = parts[regionIndex - 1];
    } else {
      city = parts.length >= 2 ? parts[parts.length - 2] : parts[parts.length - 1];
    }
  }

  return { city, region };
}

function clampPercent(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return NaN;
  if (parsed < 0) return 0;
  if (parsed > 100) return 100;
  return Number(parsed.toFixed(2));
}

function clampMoney(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return NaN;
  if (parsed < 0) return 0;
  return Number(parsed.toFixed(2));
}

function clampKm(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return NaN;
  if (parsed < 0) return 0;
  return Number(parsed.toFixed(3));
}

function normalizeCourierAdminFeeRules(rules) {
  const source = Array.isArray(rules) ? rules : [];
  const seen = new Set();
  return source
    .map((rule) => {
      const maxDeliveryFee = clampMoney(rule?.max_delivery_fee_reais);
      const percent = clampPercent(rule?.percent);
      return {
        max_delivery_fee_reais: maxDeliveryFee,
        percent,
        is_active: rule?.is_active !== false
      };
    })
    .filter((rule) => Number.isFinite(rule.max_delivery_fee_reais) && rule.max_delivery_fee_reais > 0 && Number.isFinite(rule.percent))
    .sort((a, b) => a.max_delivery_fee_reais - b.max_delivery_fee_reais)
    .filter((rule) => {
      const key = rule.max_delivery_fee_reais.toFixed(2);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function SegmentIntro({ title, description }) {
  return (
    <div className="admin-card">
      <h2 style={{ margin: 0, color: '#fff', fontSize: 28, lineHeight: 1.2 }}>{title}</h2>
      <p style={{ color: '#B8B8C5', marginTop: 10 }}>{description}</p>
    </div>
  );
}

export default function AdminFees() {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingStoreId, setSavingStoreId] = useState('');
  const [error, setError] = useState('');

  const [query, setQuery] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [storeFeeDrafts, setStoreFeeDrafts] = useState({});

  const [bulkMode, setBulkMode] = useState('city');
  const [bulkCity, setBulkCity] = useState('');
  const [bulkRegion, setBulkRegion] = useState('');
  const [bulkPercent, setBulkPercent] = useState('');

  const [data, setData] = useState({ global_percent: 10, rules: [] });
  const [deliveryData, setDeliveryData] = useState({
    global_fee_reais: 0,
    base_radius_km: 3,
    extra_km_fee_reais: 0,
    rules: [],
  });
  const [deliveryServiceData, setDeliveryServiceData] = useState({
    enabled: true,
    base_fee_reais: 8,
    base_radius_km: 3,
    extra_km_fee_reais: 1,
  });
  const [storesData, setStoresData] = useState([]);

  const [deliveryFeeDrafts, setDeliveryFeeDrafts] = useState({});
  const [bulkDeliveryMode, setBulkDeliveryMode] = useState('city');
  const [bulkDeliveryCity, setBulkDeliveryCity] = useState('');
  const [bulkDeliveryRegion, setBulkDeliveryRegion] = useState('');
  const [bulkDeliveryFee, setBulkDeliveryFee] = useState('');
  const [courierFinanceData, setCourierFinanceData] = useState({
    admin_fee_global_percent: 0,
    admin_fee_rules: []
  });
  const [newCourierRule, setNewCourierRule] = useState({
    max_delivery_fee_reais: '',
    percent: ''
  });

  const loadData = useCallback(async ({ keepLoading = false } = {}) => {
    if (keepLoading) setLoading(true);
    setError('');
    try {
      const [feesResponse, deliveryFeesResponse, storesResponse, courierFinanceResponse, enviosViagensResponse] = await Promise.all([
        fees.get(),
        deliveryFees.get(),
        stores.list({}),
        couriers.getFinanceSettings(),
        deliveryServices.getEnviosViagens(),
      ]);
      setData({
        global_percent: Number(feesResponse?.global_percent ?? 10),
        rules: Array.isArray(feesResponse?.rules) ? feesResponse.rules : []
      });
      setDeliveryData({
        global_fee_reais: Number(deliveryFeesResponse?.global_fee_reais ?? 0),
        base_radius_km: Number(deliveryFeesResponse?.base_radius_km ?? 3),
        extra_km_fee_reais: Number(deliveryFeesResponse?.extra_km_fee_reais ?? 0),
        rules: Array.isArray(deliveryFeesResponse?.rules) ? deliveryFeesResponse.rules : []
      });
      setDeliveryServiceData({
        enabled: enviosViagensResponse?.enabled !== false,
        base_fee_reais: Number(enviosViagensResponse?.base_fee_reais ?? 8),
        base_radius_km: Number(enviosViagensResponse?.base_radius_km ?? 3),
        extra_km_fee_reais: Number(enviosViagensResponse?.extra_km_fee_reais ?? 1),
      });
      setStoresData(Array.isArray(storesResponse) ? storesResponse : []);
      setCourierFinanceData({
        admin_fee_global_percent: Number(courierFinanceResponse?.admin_fee_global_percent ?? 0),
        admin_fee_rules: normalizeCourierAdminFeeRules(courierFinanceResponse?.admin_fee_rules)
      });
    } catch {
      setError('Erro ao carregar taxas e lojas.');
    } finally {
      if (keepLoading) setLoading(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    async function run() {
      if (!mounted) return;
      await loadData({ keepLoading: true });
    }
    run();
    return () => { mounted = false; };
  }, [loadData]);

  useEffect(() => {
    const section = new URLSearchParams(location.search).get('section');
    if (!section || loading) return;
    const target = document.getElementById(section);
    if (target && typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [location.search, loading]);

  const normalizedStores = useMemo(
    () => (Array.isArray(storesData) ? storesData : []).map((store) => {
      const parsed = parseCityRegion(store.address);
      const city = String(store.city || parsed.city || '').trim();
      const region = String(store.region || parsed.region || '').trim().toUpperCase();
      return {
        ...store,
        city,
        region,
        cityKey: normalizeKey(city),
        regionKey: normalizeKey(region),
      };
    }),
    [storesData]
  );

  const cities = useMemo(
    () => [...new Set(normalizedStores.map((store) => store.city).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [normalizedStores]
  );

  const regions = useMemo(
    () => [...new Set(normalizedStores.map((store) => store.region).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [normalizedStores]
  );

  const ruleMaps = useMemo(() => {
    const cityMap = new Map();
    const regionMap = new Map();

    (Array.isArray(data?.rules) ? data.rules : []).forEach((rule) => {
      if (!rule || rule.is_active === false) return;
      const percent = Number(rule.percent);
      if (!Number.isFinite(percent)) return;
      const scopeType = String(rule.scope_type || '').toLowerCase();
      const scopeId = normalizeKey(rule.scope_id);
      if (!scopeId) return;

      if (scopeType === 'city') cityMap.set(scopeId, percent);
      if (scopeType === 'region') regionMap.set(scopeId, percent);
    });

    return { cityMap, regionMap };
  }, [data?.rules]);

  const areaRules = useMemo(
    () => (Array.isArray(data?.rules) ? data.rules : []).filter((rule) => {
      if (!rule || rule.is_active === false) return false;
      const scopeType = String(rule.scope_type || '').toLowerCase();
      return scopeType === 'city' || scopeType === 'region';
    }),
    [data?.rules]
  );

  const deliveryRuleMaps = useMemo(() => {
    const storeMap = new Map();
    const cityMap = new Map();
    const regionMap = new Map();

    (Array.isArray(deliveryData?.rules) ? deliveryData.rules : []).forEach((rule) => {
      if (!rule || rule.is_active === false) return;
      const feeReais = Number(rule.fee_reais);
      if (!Number.isFinite(feeReais)) return;
      const scopeType = String(rule.scope_type || '').toLowerCase();
      const scopeId = normalizeKey(rule.scope_id);
      if (!scopeId) return;

      if (scopeType === 'store') storeMap.set(scopeId, feeReais);
      if (scopeType === 'city') cityMap.set(scopeId, feeReais);
      if (scopeType === 'region') regionMap.set(scopeId, feeReais);
    });

    return { storeMap, cityMap, regionMap };
  }, [deliveryData?.rules]);

  const deliveryAreaRules = useMemo(
    () => (Array.isArray(deliveryData?.rules) ? deliveryData.rules : []).filter((rule) => {
      if (!rule || rule.is_active === false) return false;
      const scopeType = String(rule.scope_type || '').toLowerCase();
      return scopeType === 'city' || scopeType === 'region';
    }),
    [deliveryData?.rules]
  );

  const resolveEffectiveFee = (store) => {
    const persistedPercent = Number(store?.fee_percent);
    const persistedSource = String(store?.fee_source || '').trim() || 'Global';

    if (persistedSource !== 'Loja') {
      const cityPercent = ruleMaps.cityMap.get(store.cityKey);
      if (cityPercent != null) return { percent: cityPercent, source: 'Cidade' };

      const regionPercent = ruleMaps.regionMap.get(store.regionKey);
      if (regionPercent != null) return { percent: regionPercent, source: 'Regiao' };
    }

    if (Number.isFinite(persistedPercent)) {
      return { percent: persistedPercent, source: persistedSource };
    }

    return { percent: Number(data?.global_percent || 0), source: 'Global' };
  };

  const resolveEffectiveDeliveryFee = (store) => {
    const storeRule = deliveryRuleMaps.storeMap.get(normalizeKey(store?.id));
    if (storeRule != null) return { feeReais: storeRule, source: 'Loja' };

    const cityRule = deliveryRuleMaps.cityMap.get(store.cityKey);
    if (cityRule != null) return { feeReais: cityRule, source: 'Cidade' };

    const regionRule = deliveryRuleMaps.regionMap.get(store.regionKey);
    if (regionRule != null) return { feeReais: regionRule, source: 'Regiao' };

    return { feeReais: Number(deliveryData?.global_fee_reais || 0), source: 'Global' };
  };

  const filteredStores = useMemo(() => {
    const term = normalizeKey(query);
    return normalizedStores.filter((store) => {
      const matchesTerm = !term
        || normalizeKey(store.name).includes(term)
        || normalizeKey(store.email).includes(term)
        || normalizeKey(store.address).includes(term);
      const matchesCity = !selectedCity || store.city === selectedCity;
      const matchesRegion = !selectedRegion || store.region === selectedRegion;
      return matchesTerm && matchesCity && matchesRegion;
    });
  }, [normalizedStores, query, selectedCity, selectedRegion]);

  const upsertRule = (scopeType, scopeId, percent) => {
    setData((prev) => {
      const normalizedScope = String(scopeType || '').toLowerCase();
      const idValue = String(scopeId || '').trim();
      if (!normalizedScope || !idValue) return prev;

      const nextRules = Array.isArray(prev.rules) ? [...prev.rules] : [];
      const existingIndex = nextRules.findIndex(
        (rule) => String(rule?.scope_type || '').toLowerCase() === normalizedScope
          && String(rule?.scope_id || '') === idValue
      );

      const nextRule = {
        ...(existingIndex >= 0 ? nextRules[existingIndex] : { id: `tmp-${normalizedScope}-${idValue}` }),
        scope_type: normalizedScope,
        scope_id: idValue,
        percent,
        is_active: true
      };

      if (existingIndex >= 0) nextRules[existingIndex] = nextRule;
      else nextRules.push(nextRule);

      return { ...prev, rules: nextRules };
    });
  };

  const upsertDeliveryRule = (scopeType, scopeId, feeReais) => {
    setDeliveryData((prev) => {
      const normalizedScope = String(scopeType || '').toLowerCase();
      const idValue = String(scopeId || '').trim();
      if (!normalizedScope || !idValue) return prev;

      const nextRules = Array.isArray(prev.rules) ? [...prev.rules] : [];
      const existingIndex = nextRules.findIndex(
        (rule) => String(rule?.scope_type || '').toLowerCase() === normalizedScope
          && String(rule?.scope_id || '') === idValue
      );

      const nextRule = {
        ...(existingIndex >= 0 ? nextRules[existingIndex] : { id: `tmp-delivery-${normalizedScope}-${idValue}` }),
        scope_type: normalizedScope,
        scope_id: idValue,
        fee_reais: clampMoney(feeReais),
        is_active: true
      };

      if (existingIndex >= 0) nextRules[existingIndex] = nextRule;
      else nextRules.push(nextRule);

      return { ...prev, rules: nextRules };
    });
  };

  const removeRule = (scopeType, scopeId) => {
    setData((prev) => ({
      ...prev,
      rules: (prev.rules || []).filter(
        (rule) => !(String(rule?.scope_type || '').toLowerCase() === String(scopeType || '').toLowerCase()
          && String(rule?.scope_id || '') === String(scopeId || ''))
      )
    }));
  };

  const removeDeliveryRule = (scopeType, scopeId) => {
    setDeliveryData((prev) => ({
      ...prev,
      rules: (prev.rules || []).filter(
        (rule) => !(String(rule?.scope_type || '').toLowerCase() === String(scopeType || '').toLowerCase()
          && String(rule?.scope_id || '') === String(scopeId || ''))
      )
    }));
  };

  const handleApplyStoreRule = async (store) => {
    const raw = storeFeeDrafts[store.id];
    const fallback = resolveEffectiveFee(store).percent;
    const percent = clampPercent(raw ?? fallback);
    if (!Number.isFinite(percent)) {
      alert('Informe uma porcentagem valida entre 0 e 100.');
      return;
    }

    setSavingStoreId(store.id);
    try {
      const response = await stores.updateSettings(store.id, { fee_percent_override: percent });
      const effectivePercent = Number(response?.fee_percent_effective ?? percent);
      setStoresData((prev) => prev.map((item) => (
        String(item.id) === String(store.id)
          ? { ...item, fee_percent: effectivePercent, fee_source: 'Loja' }
          : item
      )));
      setStoreFeeDrafts((prev) => ({ ...prev, [store.id]: String(effectivePercent) }));
      setData((prev) => ({
        ...prev,
        rules: (prev.rules || []).filter(
          (rule) => !(String(rule?.scope_type || '').toLowerCase() === 'store'
            && String(rule?.scope_id || '') === String(store.id))
        )
      }));
    } catch (err) {
      alert(err?.response?.data?.detail || 'Nao foi possivel salvar a taxa da loja.');
    } finally {
      setSavingStoreId('');
    }
  };

  const handleUseGlobalRule = async (store) => {
    setSavingStoreId(store.id);
    try {
      const response = await stores.updateSettings(store.id, { fee_percent_override: null });
      const effectivePercent = Number(response?.fee_percent_effective ?? data.global_percent ?? 0);
      setStoresData((prev) => prev.map((item) => (
        String(item.id) === String(store.id)
          ? { ...item, fee_percent: effectivePercent, fee_source: 'Global' }
          : item
      )));
      setStoreFeeDrafts((prev) => ({ ...prev, [store.id]: String(effectivePercent) }));
      setData((prev) => ({
        ...prev,
        rules: (prev.rules || []).filter(
          (rule) => !(String(rule?.scope_type || '').toLowerCase() === 'store'
            && String(rule?.scope_id || '') === String(store.id))
        )
      }));
    } catch (err) {
      alert(err?.response?.data?.detail || 'Nao foi possivel voltar para a regra global.');
    } finally {
      setSavingStoreId('');
    }
  };

  const handleBulkApply = () => {
    const percent = clampPercent(bulkPercent);
    if (!Number.isFinite(percent)) {
      alert('Informe uma porcentagem valida para aplicacao em lote.');
      return;
    }

    if (bulkMode === 'city') {
      const cityName = bulkCity || selectedCity;
      if (!cityName) {
        alert('Selecione a cidade para aplicar a taxa.');
        return;
      }
      upsertRule('city', cityName, percent);
      return;
    }

    if (bulkMode === 'region') {
      const regionName = bulkRegion || selectedRegion;
      if (!regionName) {
        alert('Selecione a regiao/UF para aplicar a taxa.');
        return;
      }
      upsertRule('region', regionName, percent);
      return;
    }

    alert('Use aplicacao por cidade ou regiao para salvar em lote.');
  };

  const handleApplyStoreDeliveryRule = (store) => {
    const raw = deliveryFeeDrafts[store.id];
    const fallback = resolveEffectiveDeliveryFee(store).feeReais;
    const feeReais = clampMoney(raw ?? fallback);
    if (!Number.isFinite(feeReais)) {
      alert('Informe um frete valido maior ou igual a zero.');
      return;
    }
    upsertDeliveryRule('store', store.id, feeReais);
    setDeliveryFeeDrafts((prev) => ({ ...prev, [store.id]: String(feeReais) }));
  };

  const handleUseGlobalDeliveryRule = (store) => {
    removeDeliveryRule('store', store.id);
    setDeliveryFeeDrafts((prev) => {
      const next = { ...prev };
      delete next[store.id];
      return next;
    });
  };

  const handleBulkDeliveryApply = () => {
    const feeReais = clampMoney(bulkDeliveryFee);
    if (!Number.isFinite(feeReais)) {
      alert('Informe um frete valido para aplicacao em lote.');
      return;
    }

    if (bulkDeliveryMode === 'city') {
      const cityName = bulkDeliveryCity || selectedCity;
      if (!cityName) {
        alert('Selecione a cidade para aplicar o frete.');
        return;
      }
      upsertDeliveryRule('city', cityName, feeReais);
      return;
    }

    if (bulkDeliveryMode === 'region') {
      const regionName = bulkDeliveryRegion || selectedRegion;
      if (!regionName) {
        alert('Selecione a regiao/UF para aplicar o frete.');
        return;
      }
      upsertDeliveryRule('region', regionName, feeReais);
      return;
    }

    alert('Use aplicacao por cidade ou regiao para salvar em lote.');
  };

  const handleAddCourierFeeRule = () => {
    const maxDeliveryFee = clampMoney(newCourierRule.max_delivery_fee_reais);
    const percent = clampPercent(newCourierRule.percent);
    if (!Number.isFinite(maxDeliveryFee) || maxDeliveryFee <= 0) {
      alert('Informe o valor maximo do frete (R$) para a faixa.');
      return;
    }
    if (!Number.isFinite(percent)) {
      alert('Informe o percentual da faixa entre 0 e 100.');
      return;
    }

    setCourierFinanceData((prev) => {
      const nextRules = normalizeCourierAdminFeeRules([
        ...(Array.isArray(prev.admin_fee_rules) ? prev.admin_fee_rules : []),
        { max_delivery_fee_reais: maxDeliveryFee, percent, is_active: true }
      ]);
      return { ...prev, admin_fee_rules: nextRules };
    });
    setNewCourierRule({ max_delivery_fee_reais: '', percent: '' });
  };

  const handleRemoveCourierFeeRule = (maxDeliveryFeeReais) => {
    setCourierFinanceData((prev) => ({
      ...prev,
      admin_fee_rules: (Array.isArray(prev.admin_fee_rules) ? prev.admin_fee_rules : [])
        .filter((rule) => Number(rule?.max_delivery_fee_reais) !== Number(maxDeliveryFeeReais))
    }));
  };

  const handleUpdateCourierFeeRule = (targetMaxDeliveryFeeReais, key, rawValue) => {
    setCourierFinanceData((prev) => {
      const rules = Array.isArray(prev.admin_fee_rules) ? prev.admin_fee_rules : [];
      const next = rules.map((rule) => {
        if (Number(rule?.max_delivery_fee_reais) !== Number(targetMaxDeliveryFeeReais)) return rule;
        if (key === 'max_delivery_fee_reais') {
          return { ...rule, max_delivery_fee_reais: rawValue };
        }
        if (key === 'percent') {
          return { ...rule, percent: rawValue };
        }
        return rule;
      });
      return { ...prev, admin_fee_rules: next };
    });
  };

  const handleSave = async () => {
    const globalPercent = clampPercent(data.global_percent);
    if (!Number.isFinite(globalPercent)) {
      alert('Defina uma taxa global valida entre 0 e 100.');
      return;
    }
    const globalDeliveryFee = clampMoney(deliveryData.global_fee_reais);
    if (!Number.isFinite(globalDeliveryFee)) {
      alert('Defina um frete global valido maior ou igual a zero.');
      return;
    }
    const baseRadiusKm = clampKm(deliveryData.base_radius_km);
    if (!Number.isFinite(baseRadiusKm)) {
      alert('Defina um raio base valido (km) maior ou igual a zero.');
      return;
    }
    const extraKmFeeReais = clampMoney(deliveryData.extra_km_fee_reais);
    if (!Number.isFinite(extraKmFeeReais)) {
      alert('Defina um valor por km excedente valido (R$) maior ou igual a zero.');
      return;
    }
    const enviosBaseFee = clampMoney(deliveryServiceData.base_fee_reais);
    if (!Number.isFinite(enviosBaseFee)) {
      alert('Defina o valor base de Envios e Viagens (R$).');
      return;
    }
    const enviosBaseRadiusKm = clampKm(deliveryServiceData.base_radius_km);
    if (!Number.isFinite(enviosBaseRadiusKm)) {
      alert('Defina o raio base (km) de Envios e Viagens.');
      return;
    }
    const enviosExtraKmFee = clampMoney(deliveryServiceData.extra_km_fee_reais);
    if (!Number.isFinite(enviosExtraKmFee)) {
      alert('Defina o valor por km excedente de Envios e Viagens.');
      return;
    }
    const courierAdminFeeGlobalPercent = clampPercent(courierFinanceData.admin_fee_global_percent);
    if (!Number.isFinite(courierAdminFeeGlobalPercent)) {
      alert('Defina a regra global do motoboy entre 0 e 100.');
      return;
    }

    const payloadRules = (Array.isArray(data.rules) ? data.rules : [])
      .map((rule) => ({
        scope_type: String(rule?.scope_type || '').toLowerCase(),
        scope_id: String(rule?.scope_id || '').trim(),
        percent: clampPercent(rule?.percent),
        is_active: rule?.is_active !== false,
      }))
      .filter((rule) => (
        rule.scope_type
        && rule.scope_id
        && Number.isFinite(rule.percent)
        && rule.scope_type !== 'store'
      ));

    const deliveryPayloadRules = (Array.isArray(deliveryData.rules) ? deliveryData.rules : [])
      .map((rule) => ({
        scope_type: String(rule?.scope_type || '').toLowerCase(),
        scope_id: String(rule?.scope_id || '').trim(),
        fee_reais: clampMoney(rule?.fee_reais),
        is_active: rule?.is_active !== false,
      }))
      .filter((rule) => (
        rule.scope_type
        && rule.scope_id
        && Number.isFinite(rule.fee_reais)
        && ['store', 'city', 'region'].includes(rule.scope_type)
      ));
    const courierAdminFeeRules = normalizeCourierAdminFeeRules(
      Array.isArray(courierFinanceData.admin_fee_rules) ? courierFinanceData.admin_fee_rules : []
    );

    setSaving(true);
    try {
      await Promise.all([
        fees.update({
          global_percent: globalPercent,
          rules: payloadRules
        }),
        deliveryFees.update({
          global_fee_reais: globalDeliveryFee,
          base_radius_km: baseRadiusKm,
          extra_km_fee_reais: extraKmFeeReais,
          rules: deliveryPayloadRules
        }),
        deliveryServices.updateEnviosViagens({
          enabled: deliveryServiceData.enabled !== false,
          base_fee_reais: enviosBaseFee,
          base_radius_km: enviosBaseRadiusKm,
          extra_km_fee_reais: enviosExtraKmFee,
        }),
        couriers.updateFinanceSettings({
          admin_fee_global_percent: courierAdminFeeGlobalPercent,
          admin_fee_rules: courierAdminFeeRules
        })
      ]);
      await loadData();
      alert('Taxas, fretes, Envios e Viagens e regra do motoboy atualizados com sucesso.');
    } catch (err) {
      alert(err?.response?.data?.detail || 'Nao foi possivel salvar as configuracoes.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  return (
    <div className="admin-section">
      <PageHeader
        title="Taxas e Regras"
        subtitle="Configure taxa administrativa, frete e regra de desconto silencioso do motoboy"
        actions={(
          <button className="admin-button" onClick={handleSave} disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar alteracoes'}
          </button>
        )}
      />

      <SegmentIntro
        title="Taxa administrativa (%)"
        description="Percentual do Correfood cobrado sobre o valor dos itens do pedido. Essa taxa afeta a receita do admin e o repasse liquido da loja."
      />

      <div className="admin-card">
        <h3>Taxa administrativa global (%)</h3>
        <p style={{ color: '#B8B8C5', marginBottom: 12 }}>
          Regra base da taxa administrativa quando nao existir regra especifica.
        </p>
        <div className="admin-filters">
          <input
            className="admin-input"
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={data.global_percent}
            onChange={(e) => setData((prev) => ({ ...prev, global_percent: e.target.value }))}
          />
        </div>
      </div>

      <div className="admin-card">
        <h3>Aplicacao em lote - taxa administrativa</h3>
        <p style={{ color: '#B8B8C5', marginBottom: 12 }}>
          Aplique a mesma porcentagem para varias lojas por cidade, regiao ou filtro atual.
        </p>
        <div className="admin-filters">
          <select className="admin-select" value={bulkMode} onChange={(e) => setBulkMode(e.target.value)}>
            <option value="city">Por cidade</option>
            <option value="region">Por regiao/UF</option>
          </select>

          <select
            className="admin-select"
            value={bulkCity}
            onChange={(e) => setBulkCity(e.target.value)}
            disabled={bulkMode !== 'city'}
          >
            <option value="">Selecione a cidade</option>
            {cities.map((city) => <option key={city} value={city}>{city}</option>)}
          </select>

          <select
            className="admin-select"
            value={bulkRegion}
            onChange={(e) => setBulkRegion(e.target.value)}
            disabled={bulkMode !== 'region'}
          >
            <option value="">Selecione a regiao/UF</option>
            {regions.map((region) => <option key={region} value={region}>{region}</option>)}
          </select>

          <input
            className="admin-input"
            type="number"
            min="0"
            max="100"
            step="0.01"
            placeholder="Percentual (%)"
            value={bulkPercent}
            onChange={(e) => setBulkPercent(e.target.value)}
          />

          <button className="admin-button secondary" onClick={handleBulkApply}>
            Aplicar percentual
          </button>
        </div>
      </div>

      <div className="admin-card">
        <h3>Regras de taxa administrativa por cidade e regiao</h3>
        {areaRules.length ? (
          <div className="admin-fees-rules-grid">
            {areaRules.map((rule) => (
              <div key={`${rule.scope_type}-${rule.scope_id}`} className="admin-fees-rule-item">
                <div>
                  <div className="admin-fees-rule-title">
                    {String(rule.scope_type).toLowerCase() === 'city' ? 'Cidade' : 'Regiao'}
                  </div>
                  <div className="admin-fees-rule-meta">{rule.scope_id}</div>
                </div>
                <div className="admin-fees-rule-right">
                  <span className="admin-badge success">{Number(rule.percent).toFixed(2)}%</span>
                  <button
                    className="admin-button secondary"
                    type="button"
                    onClick={() => removeRule(rule.scope_type, rule.scope_id)}
                  >
                    Remover
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ color: '#B8B8C5' }}>Nenhuma regra por cidade/regiao ativa.</p>
        )}
      </div>

      <SegmentIntro
        title="Frete (R$)"
        description="Valor de entrega cobrado do cliente. Quando a entrega usa motoboy do Correfood, este valor segue as regras de frete configuradas aqui."
      />

      <div className="admin-card">
        <h3>Frete global do Correfood (R$)</h3>
        <p style={{ color: '#B8B8C5', marginBottom: 12 }}>
          Usado quando a loja opera com motoboy do Correfood ou quando houver fallback de motoboy proprio.
        </p>
        <div className="admin-filters">
          <input
            className="admin-input"
            type="number"
            min="0"
            step="0.01"
            value={deliveryData.global_fee_reais}
            onChange={(e) => setDeliveryData((prev) => ({ ...prev, global_fee_reais: e.target.value }))}
          />
        </div>
      </div>

      <div className="admin-card">
        <h3>Regra de distancia (km excedente)</h3>
        <p style={{ color: '#B8B8C5', marginBottom: 12 }}>
          Exemplo: frete base R$ 8,00 ate 3 km, e acima disso soma R$ 1,00 por km excedente (arredondado para cima por km iniciado).
        </p>
        <div className="admin-filters">
          <input
            className="admin-input"
            type="number"
            min="0"
            step="0.1"
            value={deliveryData.base_radius_km}
            onChange={(e) => setDeliveryData((prev) => ({ ...prev, base_radius_km: e.target.value }))}
            placeholder="Raio base (km)"
          />
          <input
            className="admin-input"
            type="number"
            min="0"
            step="0.01"
            value={deliveryData.extra_km_fee_reais}
            onChange={(e) => setDeliveryData((prev) => ({ ...prev, extra_km_fee_reais: e.target.value }))}
            placeholder="Valor por km excedente (R$)"
          />
        </div>
      </div>

      <div className="admin-card" id="envios-viagens">
        <h3>Envios e Viagens</h3>
        <p style={{ color: '#B8B8C5', marginBottom: 12 }}>
          Regra exclusiva para corridas de envio e viagem do perfil do cliente.
          Funciona igual ao frete: valor base ate o raio definido + valor por km excedente.
        </p>
        <div className="admin-filters">
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: '#D5D9E3' }}>
            <input
              type="checkbox"
              checked={deliveryServiceData.enabled !== false}
              onChange={(event) => setDeliveryServiceData((prev) => ({ ...prev, enabled: event.target.checked }))}
            />
            Servico ativo
          </label>
          <input
            className="admin-input"
            type="number"
            min="0"
            step="0.01"
            value={deliveryServiceData.base_fee_reais}
            onChange={(event) => setDeliveryServiceData((prev) => ({ ...prev, base_fee_reais: event.target.value }))}
            placeholder="Valor base (R$)"
          />
          <input
            className="admin-input"
            type="number"
            min="0"
            step="0.1"
            value={deliveryServiceData.base_radius_km}
            onChange={(event) => setDeliveryServiceData((prev) => ({ ...prev, base_radius_km: event.target.value }))}
            placeholder="Raio base (km)"
          />
          <input
            className="admin-input"
            type="number"
            min="0"
            step="0.01"
            value={deliveryServiceData.extra_km_fee_reais}
            onChange={(event) => setDeliveryServiceData((prev) => ({ ...prev, extra_km_fee_reais: event.target.value }))}
            placeholder="Valor km excedente (R$)"
          />
        </div>
      </div>

      <div className="admin-card">
        <h3>Aplicacao de frete em lote</h3>
        <p style={{ color: '#B8B8C5', marginBottom: 12 }}>
          Defina o valor de frete do Correfood por cidade ou regiao.
        </p>
        <div className="admin-filters">
          <select className="admin-select" value={bulkDeliveryMode} onChange={(e) => setBulkDeliveryMode(e.target.value)}>
            <option value="city">Por cidade</option>
            <option value="region">Por regiao/UF</option>
          </select>

          <select
            className="admin-select"
            value={bulkDeliveryCity}
            onChange={(e) => setBulkDeliveryCity(e.target.value)}
            disabled={bulkDeliveryMode !== 'city'}
          >
            <option value="">Selecione a cidade</option>
            {cities.map((city) => <option key={`delivery-city-${city}`} value={city}>{city}</option>)}
          </select>

          <select
            className="admin-select"
            value={bulkDeliveryRegion}
            onChange={(e) => setBulkDeliveryRegion(e.target.value)}
            disabled={bulkDeliveryMode !== 'region'}
          >
            <option value="">Selecione a regiao/UF</option>
            {regions.map((region) => <option key={`delivery-region-${region}`} value={region}>{region}</option>)}
          </select>

          <input
            className="admin-input"
            type="number"
            min="0"
            step="0.01"
            placeholder="Frete (R$)"
            value={bulkDeliveryFee}
            onChange={(e) => setBulkDeliveryFee(e.target.value)}
          />

          <button className="admin-button secondary" onClick={handleBulkDeliveryApply}>
            Aplicar frete
          </button>
        </div>
      </div>

      <div className="admin-card">
        <h3>Regras de frete por cidade e regiao</h3>
        {deliveryAreaRules.length ? (
          <div className="admin-fees-rules-grid">
            {deliveryAreaRules.map((rule) => (
              <div key={`delivery-${rule.scope_type}-${rule.scope_id}`} className="admin-fees-rule-item">
                <div>
                  <div className="admin-fees-rule-title">
                    {String(rule.scope_type).toLowerCase() === 'city' ? 'Cidade' : 'Regiao'}
                  </div>
                  <div className="admin-fees-rule-meta">{rule.scope_id}</div>
                </div>
                <div className="admin-fees-rule-right">
                  <span className="admin-badge success">R$ {Number(rule.fee_reais || 0).toFixed(2)}</span>
                  <button
                    className="admin-button secondary"
                    type="button"
                    onClick={() => removeDeliveryRule(rule.scope_type, rule.scope_id)}
                  >
                    Remover
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ color: '#B8B8C5' }}>Nenhuma regra de frete por cidade/regiao ativa.</p>
        )}
      </div>

      <SegmentIntro
        title="Desconto silencioso do frete do motoboy (%)"
        description="Regra de percentual sobre o frete do motoboy Correfood (por faixa e global). O valor descontado acumula na carteira do Correfood."
      />

      <div className="admin-card">
        <h3>Desconto silencioso do frete do motoboy (%)</h3>
        <p style={{ color: '#B8B8C5', marginBottom: 12 }}>
          Esta regra substitui o desconto fixo por entrega. O sistema aplica a primeira faixa ativa que cobre o valor do frete.
          Se nenhuma faixa cobrir, aplica a regra global.
        </p>
        <div className="admin-filters">
          <label style={{ display: 'grid', gap: 6, minWidth: 240 }}>
            <span style={{ color: '#B8B8C5', fontSize: 12 }}>Regra global (%)</span>
            <input
              className="admin-input"
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={courierFinanceData.admin_fee_global_percent}
              onChange={(event) => setCourierFinanceData((prev) => ({
                ...prev,
                admin_fee_global_percent: event.target.value
              }))}
              placeholder="Ex.: 10"
            />
          </label>
        </div>

        <div className="admin-fees-table-wrap" style={{ marginTop: 14 }}>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Frete ate (R$)</th>
                <th>Desconto (%)</th>
                <th>Status</th>
                <th>Acoes</th>
              </tr>
            </thead>
            <tbody>
              {(Array.isArray(courierFinanceData.admin_fee_rules) ? courierFinanceData.admin_fee_rules : []).map((rule) => (
                <tr key={`courier-fee-rule-${Number(rule.max_delivery_fee_reais).toFixed(2)}`}>
                  <td>
                    <input
                      className="admin-input"
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={rule.max_delivery_fee_reais}
                      onChange={(event) => handleUpdateCourierFeeRule(rule.max_delivery_fee_reais, 'max_delivery_fee_reais', event.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      className="admin-input"
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={rule.percent}
                      onChange={(event) => handleUpdateCourierFeeRule(rule.max_delivery_fee_reais, 'percent', event.target.value)}
                    />
                  </td>
                  <td>
                    <span className={`admin-badge ${rule.is_active === false ? 'neutral' : 'success'}`}>
                      {rule.is_active === false ? 'Inativa' : 'Ativa'}
                    </span>
                  </td>
                  <td>
                    <button
                      className="admin-button secondary"
                      type="button"
                      onClick={() => handleRemoveCourierFeeRule(rule.max_delivery_fee_reais)}
                    >
                      Remover
                    </button>
                  </td>
                </tr>
              ))}
              {(Array.isArray(courierFinanceData.admin_fee_rules) ? courierFinanceData.admin_fee_rules : []).length === 0 && (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center', color: '#B8B8C5' }}>
                    Nenhuma regra por faixa ativa.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="admin-filters" style={{ marginTop: 12 }}>
          <input
            className="admin-input"
            type="number"
            min="0.01"
            step="0.01"
            placeholder="Frete maximo (R$)"
            value={newCourierRule.max_delivery_fee_reais}
            onChange={(event) => setNewCourierRule((prev) => ({ ...prev, max_delivery_fee_reais: event.target.value }))}
          />
          <input
            className="admin-input"
            type="number"
            min="0"
            max="100"
            step="0.01"
            placeholder="Percentual (%)"
            value={newCourierRule.percent}
            onChange={(event) => setNewCourierRule((prev) => ({ ...prev, percent: event.target.value }))}
          />
          <button className="admin-button secondary" type="button" onClick={handleAddCourierFeeRule}>
            Adicionar faixa
          </button>
        </div>
      </div>

      <div className="admin-card">
        <h3>Filtrar lojas</h3>
        <div className="admin-filters">
          <input
            className="admin-input"
            placeholder="Buscar por loja, e-mail ou endereco"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <select className="admin-select" value={selectedCity} onChange={(e) => setSelectedCity(e.target.value)}>
            <option value="">Todas as cidades</option>
            {cities.map((city) => <option key={city} value={city}>{city}</option>)}
          </select>
          <select className="admin-select" value={selectedRegion} onChange={(e) => setSelectedRegion(e.target.value)}>
            <option value="">Todas as regioes/UF</option>
            {regions.map((region) => <option key={region} value={region}>{region}</option>)}
          </select>
        </div>
      </div>

      <div className="admin-card">
        <h3>Taxa administrativa por estabelecimento</h3>
        <div className="admin-fees-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Loja</th>
                <th>Cidade</th>
                <th>Regiao/UF</th>
                <th>Taxa atual</th>
                <th>Origem</th>
                <th>Nova taxa (%)</th>
                <th>Acoes</th>
              </tr>
            </thead>
            <tbody>
              {filteredStores.map((store) => {
                const effective = resolveEffectiveFee(store);
                return (
                  <tr key={store.id}>
                    <td>
                      <div style={{ color: '#fff', fontWeight: 600 }}>{store.name}</div>
                      <div style={{ color: '#B8B8C5', fontSize: 12 }}>{store.address || 'Endereco nao informado'}</div>
                    </td>
                    <td>{store.city || '-'}</td>
                    <td>{store.region || '-'}</td>
                    <td>{Number(effective.percent).toFixed(2)}%</td>
                    <td>
                      <span className={`admin-badge ${effective.source === 'Global' ? 'neutral' : 'success'}`}>
                        {effective.source}
                      </span>
                    </td>
                    <td>
                      <input
                        className="admin-input"
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={storeFeeDrafts[store.id] ?? effective.percent}
                        onChange={(e) => setStoreFeeDrafts((prev) => ({ ...prev, [store.id]: e.target.value }))}
                      />
                    </td>
                    <td>
                      <div className="admin-fees-actions">
                        <button
                          className="admin-button secondary"
                          onClick={() => handleApplyStoreRule(store)}
                          disabled={savingStoreId === store.id}
                        >
                          {savingStoreId === store.id ? 'Salvando...' : 'Aplicar'}
                        </button>
                        <button
                          className="admin-button secondary"
                          onClick={() => handleUseGlobalRule(store)}
                          disabled={savingStoreId === store.id}
                        >
                          Usar regra geral
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!filteredStores.length && (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', color: '#B8B8C5' }}>
                    Nenhuma loja encontrada para o filtro atual.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="admin-card">
        <h3>Frete por estabelecimento (Correfood)</h3>
        <div className="admin-fees-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Loja</th>
                <th>Cidade</th>
                <th>Regiao/UF</th>
                <th>Frete atual</th>
                <th>Origem</th>
                <th>Novo frete (R$)</th>
                <th>Acoes</th>
              </tr>
            </thead>
            <tbody>
              {filteredStores.map((store) => {
                const effective = resolveEffectiveDeliveryFee(store);
                return (
                  <tr key={`delivery-${store.id}`}>
                    <td>
                      <div style={{ color: '#fff', fontWeight: 600 }}>{store.name}</div>
                      <div style={{ color: '#B8B8C5', fontSize: 12 }}>{store.address || 'Endereco nao informado'}</div>
                    </td>
                    <td>{store.city || '-'}</td>
                    <td>{store.region || '-'}</td>
                    <td>R$ {Number(effective.feeReais || 0).toFixed(2)}</td>
                    <td>
                      <span className={`admin-badge ${effective.source === 'Global' ? 'neutral' : 'success'}`}>
                        {effective.source}
                      </span>
                    </td>
                    <td>
                      <input
                        className="admin-input"
                        type="number"
                        min="0"
                        step="0.01"
                        value={deliveryFeeDrafts[store.id] ?? effective.feeReais}
                        onChange={(e) => setDeliveryFeeDrafts((prev) => ({ ...prev, [store.id]: e.target.value }))}
                      />
                    </td>
                    <td>
                      <div className="admin-fees-actions">
                        <button
                          className="admin-button secondary"
                          onClick={() => handleApplyStoreDeliveryRule(store)}
                        >
                          Aplicar
                        </button>
                        <button
                          className="admin-button secondary"
                          onClick={() => handleUseGlobalDeliveryRule(store)}
                        >
                          Usar regra geral
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!filteredStores.length && (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', color: '#B8B8C5' }}>
                    Nenhuma loja encontrada para o filtro atual.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
